import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router";
import { toast } from "react-toastify";
import LoadingIcon from "src/Components/Common/LoadingIcon";
import { API_ENDPOINTS } from "src/Utils/ApiConstants/ApiUrlConstants";
import { LoggedInSkeleton } from "../../Common/LoggedInSkeleton";
import { getReconTopBarButtons } from "../../Common/TopNavBar";
import useFetch from "../../Common/useFetch";
import { userContext } from "../../Contexts/userContext";
import CustomToast from "../../CustomToast";
import {
  apiErrorMessage,
  BdpoList,
  EntityRow,
  Kpis,
  PanRow,
  paymentAutomationApi as api,
  PayStatus,
  ReportStatus,
  ResultsQuery,
  RunSummary,
  SorList,
} from "./paymentAutomationApi";
import "./VendorPaymentAutomation.scss";

type PaneKey = "gen" | "log" | "exc" | "bdpo" | "sor" | "lbl";
type SortKey = "payable" | "dueInvoices" | "creditToAdjust" | "netDue" | "holds" | "grandTotal" | "amountToPay";

const PAGE_SIZE = 50;
const POLL_MS = 10000;

const STATUS_LABEL: Record<PayStatus, string> = {
  releasing: "Releasing",
  held_full_hold: "Held – Full Payment Hold",
  held_gst: "Held – GST Cancelled",
  zero_net: "₹0 – Nothing Due",
};

const DEFINITIONS: Array<[string, string]> = [
  [
    "Adjusted Closing Balance",
    "Books closing balance (ledger sign) + payment-in-progress − BDPO add-back. Payable = its magnitude.",
  ],
  ["Net Due", "Due invoices − credit-to-adjust − entity hold − other hold − PIP + BDPO."],
  ["Releasable (entity)", "MIN(Adjusted CB payable, Net Due), or 0 if a full / GST hold applies."],
  ["Grand Total", "Sum of entity releasables, including negatives (negative drag)."],
  ["Amount to Pay", "MAX(0, Grand Total) — what can be released for the PAN."],
  ["Bound By", "Which side set the minimum per entity (Adjusted CB / Net Due / Held)."],
  ["BDPO Direct-Pay", "Vendors who pay promo (BDPO) directly to Instamart; promo credits added back to CB."],
  ["Due date basis", "Cut-off for which invoices count as due (T-10 … T+10)."],
];

const BASIS_VALUES = Array.from({ length: 21 }, (_, i) => {
  const v = i - 10;
  return v === 0 ? "T" : v > 0 ? `T+${v}` : `T${v}`;
});
const DEFAULT_BASIS = "T+1";

// UI column -> backend sortKey (payable is the magnitude of adjustedCb, so its direction flips)
const SORT_MAP: Record<SortKey, { key: string; flip?: boolean }> = {
  payable: { key: "adjustedCb", flip: true },
  dueInvoices: { key: "dueInvoices" },
  creditToAdjust: { key: "creditToAdjust" },
  netDue: { key: "netDue" },
  holds: { key: "holds" },
  grandTotal: { key: "grandTotal" },
  amountToPay: { key: "amountToPay" },
};

const money = (n: number | null | undefined) => {
  if (n === null || n === undefined) return "–";
  const v = Math.round(n);
  return (v < 0 ? "-" : "") + "₹" + Math.abs(v).toLocaleString("en-IN");
};
const istDateTime = (iso: string | null | undefined) =>
  iso
    ? new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", dateStyle: "medium", timeStyle: "short" }) +
      " IST"
    : "–";
const notify = (message: string) => toast.error(<CustomToast message={message} />);
const notifyInfo = (message: string) => toast.info(<CustomToast message={message} />);
const notifyOk = (message: string) => toast.success(<CustomToast message={message} />);

const DownloadIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2z" />
  </svg>
);
const CheckIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z" />
  </svg>
);
const UploadIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M9 16h6v-6h4l-7-7-7 7h4zM5 18h14v2H5z" />
  </svg>
);

// Inline so checkbox + name always sit on one row regardless of app-wide label/input styles
const optRowStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: 9,
  width: "100%",
  margin: 0,
};
const optBoxStyle: React.CSSProperties = {
  flex: "0 0 auto",
  width: 16,
  height: 16,
  minWidth: 16,
  margin: 0,
  padding: 0,
  display: "inline-block",
};

const VendorPaymentAutomation = () => {
  const location = useLocation<any>();
  const { actor } = useContext(userContext);

  const [pane, setPane] = useState<PaneKey>("gen");
  const [showInfo, setShowInfo] = useState(false);
  const [downloading, setDownloading] = useState<"treasury" | "detailed" | null>(null);

  // generation status
  const [reportStatus, setReportStatus] = useState<ReportStatus | null>(null);
  const [basis, setBasis] = useState(DEFAULT_BASIS);
  const generating = reportStatus?.status === "Generating Report";
  const lastGeneratedBefore = useRef<string | null>(null);
  const awaitingRun = useRef(false);

  // results
  const [run, setRun] = useState<RunSummary | null>(null);
  const [kpis, setKpis] = useState<Kpis | null>(null);
  const [rows, setRows] = useState<PanRow[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loadingRows, setLoadingRows] = useState(false);
  const [selEntities, setSelEntities] = useState<string[]>([]);
  const [msOpen, setMsOpen] = useState(false);
  const [entitySearch, setEntitySearch] = useState("");
  const msRef = useRef<HTMLDivElement>(null);
  const [entityNames, setEntityNames] = useState<string[]>([]);
  const [status, setStatus] = useState("releasing");
  const [bdpoFilter, setBdpoFilter] = useState("");
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("amountToPay");
  const [sortDir, setSortDir] = useState<"ASC" | "DESC">("DESC");
  const [offset, setOffset] = useState(0);
  const reqId = useRef(0);

  // drawer
  const [selected, setSelected] = useState<PanRow | null>(null);
  const [selectedEntities, setSelectedEntities] = useState<EntityRow[] | null>(null);

  // upload lists
  const [bdpo, setBdpo] = useState<BdpoList | null>(null);
  const [sor, setSor] = useState<SorList | null>(null);
  const [listSearch, setListSearch] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadedNeedsRegen, setUploadedNeedsRegen] = useState(false);
  const bdpoInput = useRef<HTMLInputElement>(null);
  const sorInput = useRef<HTMLInputElement>(null);

  const infoRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (infoRef.current && !infoRef.current.contains(e.target as Node)) setShowInfo(false);
      if (msRef.current && !msRef.current.contains(e.target as Node)) {
        setMsOpen(false);
        setEntitySearch("");
      }
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  // debounce the search box
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(query);
      setOffset(0);
    }, 400);
    return () => clearTimeout(t);
  }, [query]);

  const currentQuery = useCallback(
    (): ResultsQuery => ({
      status,
      entityName: selEntities,
      search,
      sortKey: SORT_MAP[sortKey].key,
      sortDir: SORT_MAP[sortKey].flip ? (sortDir === "ASC" ? "DESC" : "ASC") : sortDir,
      limit: PAGE_SIZE,
      offset,
    }),
    [status, selEntities, search, sortKey, sortDir, offset]
  );

  const loadResults = useCallback(async () => {
    const id = ++reqId.current;
    setLoadingRows(true);
    try {
      const data = await api.results(currentQuery());
      if (id !== reqId.current) return;
      setRun(data.run);
      setKpis(data.kpis);
      setRows(data.rows);
      setTotalRows(data.totalRows);
    } catch (e) {
      if (id === reqId.current) notify(await apiErrorMessage(e, "Could not load payment results."));
    } finally {
      if (id === reqId.current) setLoadingRows(false);
    }
  }, [currentQuery]);

  // status: initial load + polling while generating
  const refreshStatus = useCallback(async () => {
    try {
      const s = await api.status();
      setReportStatus(s);
      return s;
    } catch (e) {
      notify(await apiErrorMessage(e, "Could not fetch report status."));
      return null;
    }
  }, []);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  useEffect(() => {
    if (!generating) return;
    const t = setInterval(async () => {
      const s = await refreshStatus();
      if (s && s.status !== "Generating Report" && awaitingRun.current) {
        awaitingRun.current = false;
        if (s.lastGeneratedAt && s.lastGeneratedAt !== lastGeneratedBefore.current) {
          notifyOk("Payment file generated.");
          setOffset(0);
          loadResults();
        } else {
          notify("Report generation failed, please retry.");
        }
      }
    }, POLL_MS);
    return () => clearInterval(t);
  }, [generating, refreshStatus, loadResults]);

  // load table whenever filters / sort / page change (once a report exists and isn't mid-generation on first load)
  const canLoad = reportStatus !== null && reportStatus.status !== "Not Generated";
  useEffect(() => {
    if (canLoad) loadResults();
  }, [canLoad, loadResults]);

  // Entity list: same companies API the Ledger Download screen uses
  useEffect(() => {
    useFetch<{ companies: Array<{ companyId: string; companyName: string }> }>(
      API_ENDPOINTS.LIST_COMPANIES.url,
      "GET",
      {
        failureMessage: API_ENDPOINTS.LIST_COMPANIES.failureMessage,
        thenCallBack: (response) => {
          const names = (response?.data?.companies ?? []).map((c) => c.companyName).filter(Boolean);
          setEntityNames(Array.from(new Set(names)).sort((a, b) => a.localeCompare(b)));
        },
      }
    );
  }, []);

  const loadBdpo = useCallback(async () => {
    try {
      setBdpo(await api.bdpo());
    } catch (e) {
      notify(await apiErrorMessage(e, "Could not load the BDPO list."));
    }
  }, []);
  const loadSor = useCallback(async () => {
    try {
      setSor(await api.sor());
    } catch (e) {
      notify(await apiErrorMessage(e, "Could not load the SOR list."));
    }
  }, []);
  useEffect(() => {
    loadBdpo();
    loadSor();
  }, [loadBdpo, loadSor]);

  const runGenerate = async () => {
    lastGeneratedBefore.current = reportStatus?.lastGeneratedAt ?? null;
    awaitingRun.current = true;
    setReportStatus((s) => ({
      status: "Generating Report",
      lastGeneratedAt: s?.lastGeneratedAt ?? null,
      dueDateBasis: s?.dueDateBasis ?? null,
    }));
    setUploadedNeedsRegen(false);
    // Generation outlives the gateway timeout: ignore 502/504/network errors and rely on /status polling.
    api.generate(basis).catch(async (e: any) => {
      const code = e?.response?.status;
      if (code === 400) {
        awaitingRun.current = false;
        notify(await apiErrorMessage(e, "Invalid due date basis."));
        refreshStatus();
      }
      // 409 = already generating; any other error: keep polling
    });
  };

  const download = async (kind: "treasury" | "detailed") => {
    setDownloading(kind);
    try {
      await api.download(kind, currentQuery());
    } catch (e) {
      notify(await apiErrorMessage(e, "Could not download the file."));
    } finally {
      setDownloading(null);
    }
  };

  const openPan = async (x: PanRow) => {
    setSelected(x);
    setSelectedEntities(null);
    try {
      const d = await api.panDetail(x.pan);
      setSelected(d.pan);
      setSelectedEntities(d.entities);
    } catch (e) {
      notify(await apiErrorMessage(e, "Could not load the entity-wise breakup."));
      setSelected(null);
    }
  };

  const onUpload = async (kind: "bdpo" | "sor", file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    try {
      if (kind === "bdpo") {
        const r = await api.uploadBdpo(file);
        notifyOk(`BDPO list uploaded — ${r.partyCount} parties (${r.activeCount} active).`);
        await loadBdpo();
      } else {
        const r = await api.uploadSor(file);
        notifyOk(`SOR list uploaded — ${r.codeCount} vendor codes.`);
        await loadSor();
      }
      setUploadedNeedsRegen(true);
    } catch (e) {
      notify(await apiErrorMessage(e, "Upload failed. Please try again."));
    } finally {
      setUploading(false);
      if (bdpoInput.current) bdpoInput.current.value = "";
      if (sorInput.current) sorInput.current.value = "";
    }
  };

  const reset = () => {
    setSelEntities([]);
    setBasis(DEFAULT_BASIS);
    setStatus("releasing");
    setBdpoFilter("");
    setQuery("");
    setSearch("");
    setSortKey("amountToPay");
    setSortDir("DESC");
    setOffset(0);
  };

  const sortBy = (k: SortKey) => {
    if (sortKey === k) setSortDir((d) => (d === "ASC" ? "DESC" : "ASC"));
    else {
      setSortKey(k);
      setSortDir("DESC");
    }
    setOffset(0);
  };

  const th = (k: SortKey, text: string) => (
    <th className={sortKey === k ? "vpa-sorted" : ""} onClick={() => sortBy(k)} style={{ cursor: "pointer" }}>
      {text} <span className="vpa-arr">{sortKey === k && sortDir === "ASC" ? "▴" : "▾"}</span>
    </th>
  );

  const visibleEntities = useMemo(() => {
    const q = entitySearch.trim().toLowerCase();
    return entityNames.filter((n) => !q || n.toLowerCase().includes(q));
  }, [entityNames, entitySearch]);

  // The results API has no BDPO filter, so this narrows the rows of the loaded page only.
  const shownRows = useMemo(
    () =>
      bdpoFilter === "y" ? rows.filter((r) => r.isBdpo) : bdpoFilter === "n" ? rows.filter((r) => !r.isBdpo) : rows,
    [rows, bdpoFilter]
  );

  const hasReport = !!run;
  const bdpoFiltered = useMemo(() => {
    const q = listSearch.toLowerCase();
    return (bdpo?.parties ?? []).filter(
      (p) => !q || p.pan.toLowerCase().includes(q) || (p.name || "").toLowerCase().includes(q)
    );
  }, [bdpo, listSearch]);
  const sorFiltered = useMemo(() => {
    const q = listSearch.toLowerCase();
    return (sor?.codes ?? []).filter(
      (c) => !q || c.pan.toLowerCase().includes(q) || c.vendorCode.toLowerCase().includes(q)
    );
  }, [sor, listSearch]);

  const tabs: Array<{ key: PaneKey; text: string; count?: number }> = [
    { key: "gen", text: "Payment Generation" },
    { key: "log", text: "File Log", count: 0 },
    { key: "exc", text: "Rectification Exception", count: 0 },
    { key: "bdpo", text: "BDPO Direct Pay Vendors", count: bdpo?.activeCount ?? 0 },
    { key: "sor", text: "SOR Vendors", count: sor?.codeCount ?? 0 },
    { key: "lbl", text: "Top Vendor Labels", count: 0 },
  ];

  const emptyRow = (cols: number, text = "No data") => (
    <tr>
      <td className="vpa-empty" colSpan={cols}>
        {text}
      </td>
    </tr>
  );

  const uploadBlock = (kind: "bdpo" | "sor") => {
    const isBdpo = kind === "bdpo";
    const meta = isBdpo ? bdpo : sor;
    const count = isBdpo ? bdpo?.partyCount : sor?.codeCount;
    return (
      <div className="vpa-card vpa-filtercard">
        <input
          ref={isBdpo ? bdpoInput : sorInput}
          type="file"
          accept=".csv,.xlsx,.xls"
          style={{ display: "none" }}
          onChange={(e) => onUpload(kind, e.target.files?.[0])}
        />
        <div
          className="vpa-drop"
          onClick={() => !uploading && (isBdpo ? bdpoInput : sorInput).current?.click()}
          style={uploading ? { opacity: 0.6, cursor: "default" } : undefined}
        >
          {uploading ? <div className="vpa-spinner" style={{ margin: "0 auto" }} /> : <UploadIcon />}
          <div className="vpa-big">
            {uploading ? "Uploading…" : isBdpo ? "Upload BDPO Direct-Pay vendor list" : "Upload SOR vendor codes"}
          </div>
          <div style={{ marginTop: 6 }}>
            CSV / XLSX — columns: {isBdpo ? "PAN, Name, Is-Active" : "PAN, Vendor Code"}
          </div>
        </div>
        <div className="vpa-listmeta">
          {meta?.batchId ? (
            <>
              Active list: <b>{meta.sourceFilename || "—"}</b> · uploaded {istDateTime(meta.uploadedAt)} · {count}{" "}
              {isBdpo ? "parties" : "codes"}
            </>
          ) : isBdpo ? (
            "No BDPO list uploaded yet."
          ) : (
            "No SOR list uploaded — no vendor is excluded from Due Invoices."
          )}
        </div>
        {uploadedNeedsRegen && (
          <div className="vpa-banner">
            Applies from the next report generation.{" "}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setPane("gen");
              }}
            >
              Go to Payment Generation
            </a>
          </div>
        )}
      </div>
    );
  };

  const drawer = (x: PanRow) => {
    const row = (l: string, v: number) => (
      <div>
        <span>{l}</span>
        <b>{money(v)}</b>
      </div>
    );
    // Portal to <body> so no transformed/overflow ancestor in the app layout can clip the fixed drawer.
    return createPortal(
      <div className="vpa vpa-portal">
        <div className="vpa-scrim" onClick={() => setSelected(null)} />
        <div className="vpa-drawer">
          <div className="vpa-dwh">
            <div>
              <h2>{x.vendorName || x.pan}</h2>
              <div className="vpa-pan vpa-mono">
                {x.pan}
                {x.isBdpo ? (
                  <>
                    {" "}
                    · <span style={{ color: "var(--blue)" }}>BDPO Direct-Pay</span>
                  </>
                ) : null}
                {"  ·  "}
                <span className={`vpa-chip vpa-${x.status}`}>{STATUS_LABEL[x.status]}</span>
              </div>
            </div>
            <div className="vpa-x" onClick={() => setSelected(null)}>
              &times;
            </div>
          </div>
          <div className="vpa-dwbody">
            <div className="vpa-dsect">How the amount to pay is built</div>
            <div className="vpa-strip">
              <div className="vpa-dcard">
                <div className="vpa-t">1 · Adjusted Closing Balance (payable)</div>
                <div className="vpa-v">{money(Math.abs(x.adjustedCb))}</div>
                <div className="vpa-led">
                  ledger sign: <b style={{ color: "var(--red)" }}>{money(x.adjustedCb)}</b> (−ve = we owe)
                </div>
                <div className="vpa-comp">
                  {row("Books CB", x.booksCb)}
                  {x.bdpoAmount ? row("+ BDPO add-back", x.bdpoAmount) : null}
                  {x.paymentInProgress ? row("− Payment-in-progress", -x.paymentInProgress) : null}
                </div>
                <div className="vpa-step">↓</div>
              </div>
              <div className="vpa-dcard">
                <div className="vpa-t">2 · Net Due</div>
                <div className={`vpa-v ${x.netDue < 0 ? "vpa-neg" : ""}`}>{money(x.netDue)}</div>
                <div className="vpa-comp">
                  {row("Due invoices", x.dueInvoices)}
                  {row("− Credit-to-adjust", -x.creditToAdjust)}
                  {row("− Entity hold", -x.entityHold)}
                  {row("− Other hold", -x.otherHold)}
                </div>
                <div className="vpa-step">↓</div>
              </div>
              <div className="vpa-dcard">
                <div className="vpa-t">3 · Grand Total (Σ, incl. negatives)</div>
                <div className={`vpa-v ${x.grandTotal < 0 ? "vpa-neg" : ""}`}>{money(x.grandTotal)}</div>
                <div className="vpa-comp">
                  {row("Positive releasables", x.positiveReleasable)}
                  <div>
                    <span>Negative drag</span>
                    <b className="vpa-neg">{money(x.negativeReleasable)}</b>
                  </div>
                </div>
                <div className="vpa-step">↓</div>
              </div>
              <div className="vpa-dcard vpa-payc">
                <div className="vpa-t">4 · Amount to Pay = MAX(0, Grand Total)</div>
                <div className="vpa-v">{money(x.amountToPay)}</div>
                {x.panFlag ? (
                  <div className="vpa-badge">{x.panFlag} — whole PAN held</div>
                ) : x.amountToPay === 0 ? (
                  <div className="vpa-badge">Net ≤ 0 — zero-net</div>
                ) : null}
              </div>
            </div>
            <div className="vpa-dsect">
              Entity-wise breakup — ledger sign, matches download
              {selectedEntities ? ` (${selectedEntities.length} active)` : ""}
            </div>
            <div className="vpa-enttable">
              <table>
                <thead>
                  <tr>
                    <th className="vpa-l">Entity</th>
                    <th>Adj CB</th>
                    <th>Net Due</th>
                    <th>Releasable</th>
                    <th style={{ textAlign: "center" }}>Bound By</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedEntities === null && emptyRow(5, "Loading…")}
                  {selectedEntities?.map((e) => (
                    <tr key={e.entityName}>
                      <td className="vpa-l" style={{ fontWeight: 600, color: "var(--head)" }}>
                        {e.entityName}
                      </td>
                      <td className={`vpa-mono${e.adjustedCb < 0 ? " vpa-neg" : ""}`}>{money(e.adjustedCb)}</td>
                      <td className={`vpa-mono${e.netDue < 0 ? " vpa-neg" : ""}`}>{money(e.netDue)}</td>
                      <td
                        className="vpa-mono"
                        style={{
                          fontWeight: 600,
                          color:
                            e.releasable > 0 ? "var(--green-crisp)" : e.releasable < 0 ? "var(--red)" : "var(--faint)",
                        }}
                      >
                        {money(e.releasable)}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        {e.holdFlag ? (
                          <span className="vpa-bound vpa-held" title={e.holdFlag}>
                            Held
                          </span>
                        ) : -e.adjustedCb <= e.netDue ? (
                          <span className="vpa-bound vpa-cb">Adj CB</span>
                        ) : (
                          <span className="vpa-bound vpa-nd">Net Due</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  const statusLine = generating
    ? "Generating Report…"
    : reportStatus?.status === "Generated"
    ? `Generated · ${istDateTime(reportStatus.lastGeneratedAt)} · basis ${reportStatus.dueDateBasis ?? "–"}`
    : reportStatus?.status === "Not Generated"
    ? "No report generated yet"
    : "";
  const from = totalRows ? offset + 1 : 0;
  const to = Math.min(offset + PAGE_SIZE, totalRows);

  return (
    <LoggedInSkeleton
      topBarButtons={getReconTopBarButtons("Ledger Analytics", actor.name, location?.state?.openCollapseObj, actor)}
    >
      <div className="vpa">
        <div className="vpa-pagetitle">
          <h1>Vendor Payment Automation</h1>
          <div className="vpa-subtitle">
            Select Entity &amp; Till Due for Payment File Generation — Based on Adjusted Closing Balance and Net Due
            <span className="vpa-infobtn" ref={infoRef} onClick={() => setShowInfo((s) => !s)}>
              i
            </span>
            {showInfo && (
              <div className="vpa-popover">
                <h4>Definitions</h4>
                <dl>
                  {DEFINITIONS.map(([t, d]) => (
                    <React.Fragment key={t}>
                      <dt>{t}</dt>
                      <dd>{d}</dd>
                    </React.Fragment>
                  ))}
                </dl>
              </div>
            )}
          </div>
        </div>

        <div className="vpa-ptabs">
          {tabs.map((t) => (
            <div
              key={t.key}
              className={`vpa-ptab${pane === t.key ? " vpa-on" : ""}`}
              onClick={() => {
                setPane(t.key);
                setListSearch("");
              }}
            >
              {t.text}
              {t.count !== undefined && <span className="vpa-cnt">{t.count}</span>}
            </div>
          ))}
        </div>

        {pane === "gen" && (
          <div>
            <div className="vpa-card vpa-filtercard">
              <div className="vpa-filterrow">
                <div className="vpa-field">
                  <label>Entity (multi-select)</label>
                  <div className="vpa-ms" ref={msRef}>
                    <div className="vpa-ms-box" onClick={() => setMsOpen((o) => !o)}>
                      {selEntities.length ? `${selEntities.length} selected` : "All entities"}
                    </div>
                    {msOpen && (
                      <div className="vpa-ms-panel">
                        <input
                          className="vpa-ms-search"
                          autoFocus
                          placeholder="Search entity"
                          value={entitySearch}
                          onChange={(e) => setEntitySearch(e.target.value)}
                        />
                        {!entitySearch.trim() && (
                          <label className="vpa-ms-opt" style={optRowStyle}>
                            <input
                              type="checkbox"
                              style={optBoxStyle}
                              checked={selEntities.length === 0}
                              onChange={() => {
                                setSelEntities([]);
                                setOffset(0);
                              }}
                            />
                            <span style={{ fontWeight: 600 }}>All entities</span>
                          </label>
                        )}
                        {visibleEntities.length === 0 && <div className="vpa-ms-empty">No entities found</div>}
                        {visibleEntities.map((n) => (
                          <label className="vpa-ms-opt" style={optRowStyle} key={n}>
                            <input
                              type="checkbox"
                              style={optBoxStyle}
                              checked={selEntities.includes(n)}
                              onChange={() => {
                                setSelEntities((cur) => (cur.includes(n) ? cur.filter((v) => v !== n) : [...cur, n]));
                                setOffset(0);
                              }}
                            />
                            <span>{n}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="vpa-field">
                  <label>Due date basis (till due)</label>
                  <select
                    className="vpa-ctl"
                    style={{ minWidth: 160 }}
                    value={basis}
                    onChange={(e) => setBasis(e.target.value)}
                  >
                    {BASIS_VALUES.map((v) => (
                      <option key={v} value={v}>
                        {v}
                        {v === DEFAULT_BASIS ? " (default)" : ""}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="vpa-actions">
                <button className="vpa-btn vpa-ghost" onClick={reset}>
                  Reset
                </button>
                <button className="vpa-btn vpa-green" onClick={runGenerate} disabled={generating}>
                  <CheckIcon />
                  Generate Payment File
                </button>
                <button
                  className="vpa-btn vpa-ghost"
                  onClick={() => download("treasury")}
                  disabled={!hasReport || generating || !!downloading}
                >
                  {downloading === "treasury" ? <LoadingIcon loading={true} /> : <DownloadIcon />}
                  Download Treasury File
                </button>
                <button
                  className="vpa-btn vpa-ghost"
                  onClick={() => download("detailed")}
                  disabled={!hasReport || generating || !!downloading}
                >
                  {downloading === "detailed" ? <LoadingIcon loading={true} /> : <DownloadIcon />}
                  Download detailed
                </button>
                <button
                  className="vpa-btn vpa-maroon"
                  onClick={() => notifyInfo("Mark as Final — not available yet.")}
                  disabled={!hasReport || generating}
                >
                  <CheckIcon />
                  Mark as Final
                </button>
                <button
                  className="vpa-btn vpa-ghost"
                  onClick={() => notifyInfo("Download Invoices to Pay — not available yet.")}
                  disabled={!hasReport || generating}
                >
                  <DownloadIcon />
                  Download Invoices to Pay
                </button>
              </div>
              {(generating || statusLine) && (
                <div className={generating ? "vpa-genbar" : "vpa-statusline"}>
                  {generating && <div className="vpa-spinner" />}
                  <div className="vpa-pct" style={{ minWidth: 0 }}>
                    {statusLine}
                    {generating && " This can take several minutes — you can leave this page open."}
                  </div>
                </div>
              )}
            </div>

            <div className="vpa-kpis">
              <div className="vpa-kpi vpa-good">
                <div className="vpa-lab">Releasing (Vendor PANs)</div>
                <div className="vpa-val">
                  {kpis ? (
                    <>
                      {kpis.releasing.toLocaleString("en-IN")} <small>PANs</small>
                    </>
                  ) : (
                    "–"
                  )}
                </div>
              </div>
              <div className="vpa-kpi vpa-accent">
                <div className="vpa-lab">Amount to Pay Today</div>
                <div className="vpa-val">{kpis ? money(kpis.amountToPay) : "–"}</div>
              </div>
              <div className="vpa-kpi vpa-warn">
                <div className="vpa-lab">Held</div>
                <div className="vpa-val">
                  {kpis ? (
                    <>
                      {kpis.held.toLocaleString("en-IN")} <small>vendors</small>
                    </>
                  ) : (
                    "–"
                  )}
                </div>
                <div className="vpa-sub" />
              </div>
              <div className="vpa-kpi">
                <div className="vpa-lab">Net Due (Total)</div>
                <div className="vpa-val">{kpis ? money(kpis.netDue) : "–"}</div>
              </div>
            </div>

            <div className="vpa-resultcard">
              <div className="vpa-filterbar">
                <div className="vpa-field">
                  <label>Vendor name / PAN</label>
                  <div className="vpa-searchwrap">
                    <svg viewBox="0 0 24 24">
                      <path d="M15.5 14h-.8l-.3-.3a6.5 6.5 0 1 0-.7.7l.3.3v.8l5 5 1.5-1.5-5-5zm-6 0A4.5 4.5 0 1 1 14 9.5 4.5 4.5 0 0 1 9.5 14z" />
                    </svg>
                    <input
                      className="vpa-ctl"
                      placeholder="Search"
                      style={{ minWidth: 220 }}
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                    />
                  </div>
                </div>
                <div className="vpa-field">
                  <label>Status</label>
                  <select
                    className="vpa-ctl"
                    value={status}
                    onChange={(e) => {
                      setStatus(e.target.value);
                      setOffset(0);
                    }}
                  >
                    <option value="all">All</option>
                    <option value="releasing">Releasing</option>
                    <option value="held_full_hold">Held – Full</option>
                    <option value="held_gst">Held – GST</option>
                    <option value="zero_net">Zero-net</option>
                  </select>
                </div>
                <div className="vpa-field">
                  <label>BDPO Direct-Pay</label>
                  <select className="vpa-ctl" value={bdpoFilter} onChange={(e) => setBdpoFilter(e.target.value)}>
                    <option value="">All</option>
                    <option value="y">Yes</option>
                    <option value="n">No</option>
                  </select>
                </div>
              </div>
              <div className="vpa-rc-head">
                <h3>Top Vendors</h3>
                <span className="vpa-cnt">{totalRows.toLocaleString("en-IN")}</span>
                <div className="vpa-sp" />
                <div className="vpa-info">
                  {run
                    ? `Report of ${istDateTime(run.generatedAt)} · basis ${run.dueDateBasis} · cut-off ${
                        run.dueDateCutoff
                      }`
                    : "–"}
                </div>
              </div>
              <div className="vpa-tablescroll">
                <table>
                  <thead>
                    <tr>
                      <th className="vpa-l">Vendor / PAN</th>
                      {th("payable", "Payable")}
                      {th("dueInvoices", "Due")}
                      {th("creditToAdjust", "Credit-adj")}
                      {th("netDue", "Net Due")}
                      {th("holds", "Holds")}
                      {th("grandTotal", "Grand Total")}
                      {th("amountToPay", "Amount to Pay")}
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shownRows.length === 0 &&
                      emptyRow(
                        9,
                        loadingRows
                          ? "Loading…"
                          : hasReport
                          ? "No vendors match the filters"
                          : "No report yet — click Generate Payment File"
                      )}
                    {shownRows.map((x, i) => (
                      <tr
                        key={x.pan}
                        className="vpa-clickable"
                        style={selected?.pan === x.pan ? { background: "var(--maroon-06)" } : undefined}
                        onClick={() => openPan(x)}
                      >
                        <td className="vpa-l">
                          <span className="vpa-rank">{offset + i + 1}.</span>{" "}
                          <span className="vpa-vend">{x.vendorName || "—"}</span>
                          {x.isBdpo ? <span className="vpa-flag">BDPO DIRECT-PAY</span> : null}
                          <span className="vpa-pan vpa-mono">{x.pan}</span>
                        </td>
                        <td className="vpa-mono vpa-sec">{money(Math.abs(x.adjustedCb))}</td>
                        <td className="vpa-mono vpa-sec">{money(x.dueInvoices)}</td>
                        <td className="vpa-mono vpa-sec">{money(x.creditToAdjust)}</td>
                        <td className={`vpa-mono vpa-sec${x.netDue < 0 ? " vpa-neg" : ""}`}>{money(x.netDue)}</td>
                        <td className="vpa-mono vpa-sec">{money(x.entityHold + x.otherHold)}</td>
                        <td className={`vpa-mono vpa-sec${x.grandTotal < 0 ? " vpa-neg" : ""}`}>
                          {money(x.grandTotal)}
                        </td>
                        <td className="vpa-mono">
                          <span className="vpa-amt-hero">{money(x.amountToPay)}</span>
                        </td>
                        <td>
                          <span className={`vpa-chip vpa-${x.status}`}>{STATUS_LABEL[x.status]}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="vpa-foot">
                <div>
                  {totalRows
                    ? `${from.toLocaleString("en-IN")}–${to.toLocaleString("en-IN")} of ${totalRows.toLocaleString(
                        "en-IN"
                      )} vendors`
                    : "–"}
                  {run ? ` · basis ${run.dueDateBasis}` : ""}
                </div>
                <div className="vpa-pager">
                  <button
                    className="vpa-btn vpa-ghost vpa-sm"
                    disabled={offset === 0 || loadingRows}
                    onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                  >
                    Previous
                  </button>
                  <button
                    className="vpa-btn vpa-ghost vpa-sm"
                    disabled={to >= totalRows || loadingRows}
                    onClick={() => setOffset(offset + PAGE_SIZE)}
                  >
                    Next
                  </button>
                </div>
                <div className="vpa-sum">
                  Amount to pay (filtered): <b className="vpa-mono">{kpis ? money(kpis.amountToPay) : "–"}</b>
                </div>
              </div>
            </div>
            <div className="vpa-hint">
              Intermediate figures are muted; Amount to Pay is the decision number. Click a row for the full entity-wise
              calculation.
            </div>
          </div>
        )}

        {pane === "log" && (
          <div className="vpa-resultcard">
            <div className="vpa-rc-head">
              <h3>Payment File Log</h3>
              <span className="vpa-cnt">0</span>
              <div className="vpa-sp" />
              <div className="vpa-info">Daily finalized files — immutable snapshot + invoice export</div>
            </div>
            <div className="vpa-tablescroll">
              <table>
                <thead>
                  <tr>
                    <th className="vpa-l">Date</th>
                    <th className="vpa-l">Basis</th>
                    <th>Vendors</th>
                    <th>Amount</th>
                    <th>Invoices</th>
                    <th className="vpa-l">Finalized by</th>
                    <th className="vpa-l">Status</th>
                    <th className="vpa-l">Downloads</th>
                  </tr>
                </thead>
                <tbody>{emptyRow(8, "Not available yet")}</tbody>
              </table>
            </div>
          </div>
        )}

        {pane === "exc" && (
          <div className="vpa-resultcard">
            <div className="vpa-rc-head">
              <h3>Rectification exception report</h3>
              <span className="vpa-cnt">0</span>
            </div>
            <div className="vpa-tablescroll">
              <table>
                <thead>
                  <tr>
                    <th className="vpa-l">Cat</th>
                    <th className="vpa-l">Vendor / PAN</th>
                    <th className="vpa-l">Entity</th>
                    <th className="vpa-l">Document</th>
                    <th>Outstanding</th>
                    <th className="vpa-l">Suggested action</th>
                    <th className="vpa-l">Decision</th>
                  </tr>
                </thead>
                <tbody>{emptyRow(7, "Not available yet")}</tbody>
              </table>
            </div>
          </div>
        )}

        {pane === "lbl" && (
          <div className="vpa-resultcard">
            <div className="vpa-rc-head">
              <h3>Top vendor labels</h3>
              <span className="vpa-cnt">0</span>
            </div>
            <div className="vpa-tablescroll">
              <table>
                <thead>
                  <tr>
                    <th className="vpa-l">PAN</th>
                    <th className="vpa-l">Vendor</th>
                    <th className="vpa-l">Label</th>
                    <th>Amount to Pay</th>
                  </tr>
                </thead>
                <tbody>{emptyRow(4, "Not available yet")}</tbody>
              </table>
            </div>
          </div>
        )}

        {pane === "bdpo" && (
          <div>
            {uploadBlock("bdpo")}
            <div className="vpa-resultcard" style={{ marginTop: 18 }}>
              <div className="vpa-rc-head">
                <h3>BDPO Direct-Pay vendors</h3>
                <span className="vpa-cnt">{bdpoFiltered.length}</span>
                <div className="vpa-sp" />
                <input
                  className="vpa-ctl"
                  style={{ height: 36, minWidth: 220 }}
                  placeholder="Search PAN / name"
                  value={listSearch}
                  onChange={(e) => setListSearch(e.target.value)}
                />
              </div>
              <div className="vpa-tablescroll">
                <table>
                  <thead>
                    <tr>
                      <th className="vpa-l">PAN</th>
                      <th className="vpa-l">Name</th>
                      <th style={{ textAlign: "center" }}>Active</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bdpoFiltered.length === 0 && emptyRow(3)}
                    {bdpoFiltered.map((b) => (
                      <tr key={b.pan}>
                        <td className="vpa-l vpa-mono">{b.pan}</td>
                        <td className="vpa-l">{b.name || "—"}</td>
                        <td style={{ textAlign: "center" }}>
                          <span className={`vpa-chip ${b.isActive ? "vpa-releasing" : "vpa-zero_net"}`}>
                            {b.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {pane === "sor" && (
          <div>
            {uploadBlock("sor")}
            <div className="vpa-resultcard" style={{ marginTop: 18 }}>
              <div className="vpa-rc-head">
                <h3>SOR vendors</h3>
                <span className="vpa-cnt">{sorFiltered.length}</span>
                <div className="vpa-sp" />
                <div className="vpa-info">Excluded from Due Invoices when PAN and Vendor Code both match</div>
                <input
                  className="vpa-ctl"
                  style={{ height: 36, minWidth: 220, marginLeft: 12 }}
                  placeholder="Search PAN / vendor code"
                  value={listSearch}
                  onChange={(e) => setListSearch(e.target.value)}
                />
              </div>
              <div className="vpa-tablescroll">
                <table>
                  <thead>
                    <tr>
                      <th className="vpa-l">PAN</th>
                      <th className="vpa-l">Vendor Code</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorFiltered.length === 0 && emptyRow(2)}
                    {sorFiltered.slice(0, 500).map((c) => (
                      <tr key={c.pan + c.vendorCode}>
                        <td className="vpa-l vpa-mono">{c.pan}</td>
                        <td className="vpa-l vpa-mono">{c.vendorCode}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {sorFiltered.length > 500 && (
                <div className="vpa-foot">
                  Showing first 500 of {sorFiltered.length.toLocaleString("en-IN")} — use search to narrow.
                </div>
              )}
            </div>
          </div>
        )}

        {selected && drawer(selected)}
      </div>
    </LoggedInSkeleton>
  );
};

export default VendorPaymentAutomation;
